import { useCallback, useMemo, useState } from 'react';
import { ISammanfattningRows } from './EkonomiskAnalysSammanfattning';
import { IColumn } from '../EkonomiskAnalysTable/EkonomiskAnalysTableBase';
import EDButton from '../../../../../../../BaseComponents/Buttons/Button/EDButton';
import {
  AnalysLevelEnum,
  AnalysTypeEnum,
  DetaljStatusEnum,
  INTAKTSUPPFOLJNING_IDKOD,
  KOSTNADSUPPFOLJNING_IDKOD,
  SpecifikationTypeEnum,
  VERKSAMHETSOMRADE_SAKNAS,
} from '../../Const/EkonomiskAnalysConsts';
import { ISumData } from '../../Queries/EkonomiskAnalysSettingsQuery/EkonomiskAnalysSettingsQuery';
import { useFrontendUrl } from '../../../../../../../Hooks/useFrontendUrl';
import { IEkonomiskAnalysFilledDates } from '../AnalysSettingsHandler/AnalysSettingsHandler';
import {
  getKontodelString,
  shouldHaveGreenValue,
} from '../../Utils/detaljanalysUtils';
import { makeStyles } from 'tss-react/mui';
import { Theme } from '@mui/material';
import { findFormatter } from '../../../../../../../Utils/Formatters/FindFormatter';
import { SammanfattningStatus } from './SammanfattningStatus';
import { getCurrencyFormatterParams } from '../../Utils/paramUtils';
import { EDControlledExpansionPanel } from '../../../../../../../BaseComponents/Accordion/EDExpansionPanel';
import EkonomiskAnalysExpansionLabel from './Components/EkonomiskAnalysSammanfattningCharts/Components/EkonomiskAnalysExpansionLabel';
import { IUIUrval } from '../../../../../../../Types/UrvalTypes';
import { ISelection } from '../../Queries/EkonomiskAnalysInitQuery/EkonomiskAnalysInitQuery';
import { SUMROWID } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataTable/Consts/BusinessDataTableConsts';
import {
  borraIntaktEllerKostnad,
  redirectToNewDetaljAnalys,
} from '../../Utils/detaljAnalysDrillUtils';
import EDLink from '../../../../../../../BaseComponents/Link/EDLink';
import {
  capitalizeFirstLetter,
  getMonthText,
} from '../../../../../../../Utils/dateUtils';
import { TABLE_DISPLAY_LABEL } from './Consts/ekonomiskAnalysSammanfattningConts';
import EDDataGrid from '../../../../../../../BaseComponents/XGrid/EDDataGrid';
import mergeSort from '../../../../../../../Utils/mergeSort';
import {
  GridColDef,
  GridRenderCellParams,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import EDIconButton from '../../../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import EDPopover from '../../../../../../../BaseComponents/Popover/EDPopover';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';

interface IProps {
  analysLevel: AnalysLevelEnum;
  analysType: AnalysTypeEnum;
  availableReports: ISelection[];
  dates: IEkonomiskAnalysFilledDates;
  denomination: number;
  enhet: string;
  expandedTable: boolean;
  kolumnerRubriker: IColumn[];
  rubrikNiva: string;
  selectedAnalysCodeprivilegeID: string;
  selectedAnalysID: string;
  setExpandedTable: (value: boolean) => void;
  subLevelNiva: string | undefined;
  sums: ISumData[];
  tableRows: ISammanfattningRows[] | undefined;
  urval: IUIUrval | null;
}

const useStyles = makeStyles()((theme: Theme) => ({
  grid: {
    width: '100%',
    maxWidth: '100%',
    maxHeight: 'min(60vh, 500px)',
    boxSizing: 'border-box',
    border: '1px solid ' + theme.palette.divider,

    '& .MuiDataGrid-columnHeader': {
      fontWeight: 'bold',
      backgroundColor:
        theme.customProperties.EkonomiskAnalysRowColors.selectedRowBoxBGColor,
    },
    '& .MuiDataGrid-cell[data-field="status"]': {
      lineHeight: 'normal',
    },
    '& .status-cell': {
      alignSelf: 'center',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },

    '@media print': {
      height: 'auto !important',
      maxHeight: 'none !important',
      overflow: 'visible !important',

      '& .MuiDataGrid-main': {
        height: 'auto !important',
        overflow: 'visible !important',
      },
      '& .MuiDataGrid-virtualScroller': {
        height: 'auto !important',
        overflow: 'visible !important',
      },
      '& .MuiDataGrid-virtualScrollerContent': {
        height: 'auto !important',
      },
      '& .MuiDataGrid-virtualScrollerRenderZone': {
        transform: 'none !important',
      },

      '& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
        padding: '4px !important',
        fontSize: '9pt !important',
        maxWidth: '120px !important',
        overflow: 'hidden !important',
        textOverflow: 'ellipsis !important',
      },
      '& .MuiDataGrid-columnHeaderTitle': {
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
      },

      '& .MuiDataGrid-row': {
        breakInside: 'avoid',
        pageBreakInside: 'avoid',
        pageBreakAfter: 'auto',
      },

      '& .MuiDataGrid-cell': {
        whiteSpace: 'nowrap',
      },

      '& .status-cell': {
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
        textOverflow: 'clip',
        padding: '2px',
      },
    },
  },
  oddRow: {
    backgroundColor: theme.customProperties.EkonomiskAnalysRowColors.mediumBg,
    '&&:hover': {
      // Keep the same background color on hover for odd rows
      backgroundColor: theme.customProperties.EkonomiskAnalysRowColors.mediumBg,
    },
    '&& .MuiDataGrid-root .MuiDataGrid-row:hover': {
      // Keep the same background color on hover for odd rows
      backgroundColor: theme.customProperties.EkonomiskAnalysRowColors.mediumBg,
    },
  },
  evenRow: {
    backgroundColor: theme.customProperties.Fade.LightOnDark.highEmphasis,
    '&&:hover': {
      // Keep the same background color on hover for even rows
      backgroundColor: theme.customProperties.Fade.LightOnDark.highEmphasis,
    },
    '&& .MuiDataGrid-root .MuiDataGrid-row:hover': {
      // Keep the same background color on hover for even rows
      backgroundColor: theme.customProperties.Fade.LightOnDark.highEmphasis,
    },
  },
  redValue: {
    color: theme.customProperties.Legend.red.full,
  },
  greenValue: {
    color: theme.customProperties.Legend.darkGreen.full,
  },
  noWrap: {
    textWrap: 'noWrap',
  },
  textAlignRight: {
    textAlign: 'right',
  },
  textAlignCenter: {
    textAlign: 'center',
  },
  actionCell: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
  },
}));

interface ISubLevelGridRow extends ISammanfattningRows {
  id: string;
  headerValue: string;
  intaktValue: number | null;
  costValue: number | null;
  avvikelseProcentBudgetRiktvardeNum: number;
  isKostnadsbudget: boolean | undefined;
}

export const SammanfattningSubLevelTable = (props: IProps) => {
  const { classes, cx, theme } = useStyles();
  const frontendUrl = useFrontendUrl();
  const currencyFormatter = findFormatter('TEDFormatCurr');
  const percentFormatter = findFormatter('TEDFormatIntAsPercent');
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [popoverState, setPopoverState] = useState<{
    anchorEl: HTMLElement | null;
    rowId: string | null;
  }>({ anchorEl: null, rowId: null });

  const currencyFormatterParams = useMemo(() => {
    return getCurrencyFormatterParams(
      { fieldInfo: [] },
      props.denomination,
      0,
      'Y'
    );
  }, [props.denomination]);

  const handleOnDrill = useCallback(
    (
      rubrikNiva: string | undefined,
      selectedSubniva: string | undefined,
      clickedSublvl: string | number,
      selectedAnalysID: string,
      selectedAnalysCodeprivilegeID: string,
      urval: IUIUrval | null,
      analysType: AnalysTypeEnum
    ) => {
      //TODO: Här kommer vi istället att vilka kolla på en markering om den klickade baskontot har bokförts utan verksamhetskod
      // Se https://eniacdata.atlassian.net/browse/EK-7720
      const jumpToLastAnalysLevel =
        (analysType === AnalysTypeEnum.INTAKTSUPPFOLJNING ||
          analysType === AnalysTypeEnum.RESULTATUPPFOLJNING) &&
        clickedSublvl === VERKSAMHETSOMRADE_SAKNAS;

      redirectToNewDetaljAnalys(
        selectedAnalysID,
        props.analysLevel,
        clickedSublvl,
        props.dates,
        frontendUrl,
        jumpToLastAnalysLevel,
        rubrikNiva,
        selectedSubniva,
        selectedAnalysCodeprivilegeID,
        props.sums,
        urval,
        false
      );
    },
    [props.analysLevel, props.dates, props.sums, frontendUrl]
  );

  const getCostData = useCallback(
    (row: ISammanfattningRows) => {
      return row.prevPeriods.find(
        prevPeriod =>
          prevPeriod.periodNumber === props.dates.from.month &&
          prevPeriod.specifikation === SpecifikationTypeEnum.KOSTNAD
      );
    },
    [props.dates.from.month]
  );

  const getIntaktData = useCallback(
    (row: ISammanfattningRows) => {
      return row.prevPeriods.find(
        prevPeriod =>
          prevPeriod.periodNumber === props.dates.from.month &&
          prevPeriod.specifikation === SpecifikationTypeEnum.INTAKT
      );
    },
    [props.dates.from.month]
  );

  const formatCurrencyValue = useCallback(
    (value: number | null | undefined | string) => {
      if (
        value == null ||
        value === 0 ||
        isNaN(Number(value)) ||
        value === ''
      ) {
        return null;
      }

      return `${currencyFormatter.displayer.display(
        value,
        true,
        currencyFormatterParams
      )} ${props.enhet}`;
    },
    [currencyFormatter, currencyFormatterParams, props.enhet]
  );

  const rows = useMemo<ISubLevelGridRow[]>(() => {
    return (props.tableRows ?? []).map((row, rowIndex) => {
      const costData = getCostData(row);
      const intaktData = getIntaktData(row);

      return {
        ...row,
        id: row.id,
        headerValue: getKontodelString(row.header),
        intaktValue: intaktData?.value ?? null,
        costValue: costData?.value ?? null,
        avvikelseProcentBudgetRiktvardeNum: parseFloat(
          row.avvikelseProcentBudgetRiktvarde
        ),
        isKostnadsbudget:
          props.analysType === AnalysTypeEnum.RESULTATUPPFOLJNING
            ? row.budgetArVarde < 0
            : undefined,
      };
    });
  }, [getCostData, getIntaktData, props.analysType, props.tableRows]);

  const summaryRow = useMemo<ISubLevelGridRow | null>(() => {
    if (rows.length === 0) {
      return null;
    }

    return rows.reduce<ISubLevelGridRow>(
      (accumulator, row) => ({
        ...accumulator,
        utgaendeVarde: accumulator.utgaendeVarde + (row.utgaendeVarde || 0),
        intaktValue: (accumulator.intaktValue || 0) + (row.intaktValue || 0),
        costValue: (accumulator.costValue || 0) + (row.costValue || 0),
        budgetRiktvarde:
          accumulator.budgetRiktvarde + (row.budgetRiktvarde || 0),
        avvikelseBudgetRiktvarde:
          accumulator.avvikelseBudgetRiktvarde +
          (row.avvikelseBudgetRiktvarde || 0),
        helarsPrognos: accumulator.helarsPrognos + (row.helarsPrognos || 0),
        budgetArVarde: accumulator.budgetArVarde + (row.budgetArVarde || 0),
      }),
      {
        ...rows[0],
        id: SUMROWID,
        header: 'Summa',
        headerValue: 'Summa',
        utgaendeVarde: 0,
        intaktValue: 0,
        costValue: 0,
        budgetRiktvarde: 0,
        avvikelseBudgetRiktvarde: 0,
        avvikelseProcentBudgetRiktvarde: '0',
        avvikelseProcentBudgetRiktvardeNum: 0,
        status: undefined as unknown as DetaljStatusEnum,
        helarsPrognos: 0,
        budgetArVarde: 0,
        isKostnadsbudget: false,
        prevPeriods: [],
      }
    );
  }, [rows]);

  const sortedRows = useMemo(() => {
    const [activeSort] = sortModel;
    if (!activeSort?.field || !activeSort?.sort) {
      return summaryRow ? [...rows, summaryRow] : rows;
    }

    const direction = activeSort.sort === 'asc' ? 1 : -1;

    const sortedDataRows = mergeSort(
      [...rows],
      (a: ISubLevelGridRow, b: ISubLevelGridRow) => {
        const field = activeSort.field as keyof ISubLevelGridRow;
        const valueA = a[field];
        const valueB = b[field];

        if (valueA == null && valueB == null) {
          return 0;
        }

        if (valueA == null) {
          return 1;
        }

        if (valueB == null) {
          return -1;
        }

        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return (valueA - valueB) * direction;
        }

        return String(valueA).localeCompare(String(valueB), 'sv') * direction;
      }
    );

    return summaryRow ? [...sortedDataRows, summaryRow] : sortedDataRows;
  }, [rows, sortModel, summaryRow]);

  const columns = useMemo<GridColDef<ISubLevelGridRow>[]>(() => {
    const mappedColumns: GridColDef<ISubLevelGridRow>[] = [
      {
        field: 'headerValue',
        headerName: props.subLevelNiva ?? '',
        minWidth: 220,
        flex: 1,
        sortable: false,
        renderCell: params => params.row.headerValue,
      },
      {
        field: 'utgaendeVarde',
        headerName: 'Utgående värde period',
        minWidth: 170,
        align: 'right',
        headerAlign: 'right',
        sortable: true,
        renderCell: params => {
          const formattedValue = formatCurrencyValue(params.row.utgaendeVarde);

          if (params.row.id !== SUMROWID) {
            const row = props?.tableRows?.find(row => row.id === params.row.id);

            if (row == undefined) {
              return formattedValue;
            }

            const preYearPeriodData = row.prevPeriods.find(
              prevPeriod =>
                prevPeriod.periodNumber === props.dates.from.month &&
                prevPeriod.periodYear === props.dates.from.year - 1 &&
                prevPeriod.specifikation === SpecifikationTypeEnum.UNDEFINED
            );

            return (
              <>
                {formattedValue}
                <EDIconButton
                  icon="info"
                  tooltip="Förgående års värde"
                  p={0}
                  m={1}
                  onClick={event => {
                    setPopoverState(currentState => {
                      const isSameRowOpen =
                        currentState.rowId === params.row.id &&
                        currentState.anchorEl != null;

                      if (isSameRowOpen) {
                        return { anchorEl: null, rowId: null };
                      }

                      return {
                        anchorEl: event.currentTarget,
                        rowId: params.row.id,
                      };
                    });
                  }}
                />
                <EDPopover
                  open={
                    popoverState.rowId === params.row.id &&
                    Boolean(popoverState.anchorEl)
                  }
                  anchorEl={popoverState.anchorEl}
                  onClose={() => {
                    setPopoverState({ anchorEl: null, rowId: null });
                  }}
                  style={{ padding: '5px' }}
                >
                  <EDTypography>
                    {formatCurrencyValue(preYearPeriodData?.value)}
                  </EDTypography>
                </EDPopover>
              </>
            );
          } else {
            return formattedValue;
          }
        },
      },
    ];

    if (props.analysType === AnalysTypeEnum.RESULTATUPPFOLJNING) {
      mappedColumns.push(
        {
          field: 'intaktValue',
          headerName: 'Utgående intäkt period',
          minWidth: 170,
          align: 'right',
          headerAlign: 'right',
          sortable: true,
          renderCell: (params: GridRenderCellParams<ISubLevelGridRow>) => {
            const formattedIntaktValue = formatCurrencyValue(
              params.row.intaktValue
            );

            if (
              params.row.id === SUMROWID ||
              props.analysLevel === AnalysLevelEnum.LAST ||
              !formattedIntaktValue
            ) {
              return formattedIntaktValue;
            }

            return (
              <EDLink
                buttonComponent
                onClick={() => {
                  borraIntaktEllerKostnad(
                    props.rubrikNiva,
                    props.subLevelNiva,
                    params.row.header,
                    props.selectedAnalysCodeprivilegeID,
                    props.urval,
                    props.analysLevel,
                    props.availableReports,
                    props.dates,
                    INTAKTSUPPFOLJNING_IDKOD,
                    frontendUrl,
                    props.sums,
                    false,
                    props.analysType
                  );
                }}
              >
                {formattedIntaktValue}
              </EDLink>
            );
          },
        },
        {
          field: 'costValue',
          headerName: 'Utgående kostnad period',
          minWidth: 180,
          align: 'right',
          headerAlign: 'right',
          sortable: true,
          renderCell: (params: GridRenderCellParams<ISubLevelGridRow>) => {
            const formattedCostValue = formatCurrencyValue(
              params.row.costValue != null ? params.row.costValue * -1 : null
            );

            if (
              params.row.id === SUMROWID ||
              props.analysLevel === AnalysLevelEnum.LAST ||
              !formattedCostValue
            ) {
              return formattedCostValue;
            }

            return (
              <EDLink
                buttonComponent
                onClick={() => {
                  borraIntaktEllerKostnad(
                    props.rubrikNiva,
                    props.subLevelNiva,
                    params.row.header,
                    props.selectedAnalysCodeprivilegeID,
                    props.urval,
                    props.analysLevel,
                    props.availableReports,
                    props.dates,
                    KOSTNADSUPPFOLJNING_IDKOD,
                    frontendUrl,
                    props.sums,
                    false,
                    props.analysType
                  );
                }}
              >
                {formattedCostValue}
              </EDLink>
            );
          },
        }
      );
    }

    mappedColumns.push(
      {
        field: 'budgetRiktvarde',
        headerName: 'Budget riktvärde period',
        minWidth: 180,
        align: 'right',
        headerAlign: 'right',
        sortable: true,
        renderCell: params => formatCurrencyValue(params.row.budgetRiktvarde),
      },
      {
        field: 'avvikelseBudgetRiktvarde',
        headerName: 'Avvikelse budget riktvärde',
        minWidth: 190,
        align: 'right',
        headerAlign: 'right',
        sortable: true,
        cellClassName: params =>
          params.row.id === SUMROWID
            ? ''
            : shouldHaveGreenValue(
                params.row.avvikelseBudgetRiktvarde as number,
                props.analysType,
                params.row.isKostnadsbudget
              )
            ? classes.greenValue
            : classes.redValue,
        renderCell: params =>
          formatCurrencyValue(params.row.avvikelseBudgetRiktvarde),
      },
      {
        field: 'avvikelseProcentBudgetRiktvardeNum',
        headerName: 'Avvikelse budget riktvärde %',
        minWidth: 200,
        align: 'right',
        headerAlign: 'right',
        sortable: true,
        cellClassName: params =>
          params.row.id === SUMROWID
            ? ''
            : shouldHaveGreenValue(
                params.row.avvikelseBudgetRiktvarde as number,
                props.analysType,
                params.row.isKostnadsbudget
              )
            ? classes.greenValue
            : classes.redValue,
        renderCell: params =>
          params.row.avvikelseProcentBudgetRiktvardeNum !== 0
            ? percentFormatter.displayer.display(
                params.row.avvikelseProcentBudgetRiktvarde,
                true
              )
            : null,
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 170,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        renderCell: params =>
          params.row.id === SUMROWID ? null : (
            <div className="status-cell">
              <SammanfattningStatus
                status={params.row.status as DetaljStatusEnum}
                avvikelseBudgetRiktvarde={params.row.avvikelseBudgetRiktvarde}
                isKostnadInIntaktUppfoljning={params.row.isKostnadsbudget}
                analysType={props.analysType}
              />
            </div>
          ),
      },
      {
        field: 'helarsPrognos',
        headerName: 'Helårsprognos',
        minWidth: 170,
        align: 'right',
        headerAlign: 'right',
        sortable: true,
        renderCell: params => formatCurrencyValue(params.row.helarsPrognos),
      },
      {
        field: 'budgetArVarde',
        headerName: 'Årsbudget',
        minWidth: 160,
        align: 'right',
        headerAlign: 'right',
        sortable: true,
        renderCell: params => formatCurrencyValue(params.row.budgetArVarde),
      }
    );

    if (props.analysLevel !== AnalysLevelEnum.LAST) {
      mappedColumns.push({
        field: 'analyseraAction',
        headerName: '',
        minWidth: 130,
        sortable: false,
        filterable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params: GridRenderCellParams<ISubLevelGridRow>) =>
          params.row.id === SUMROWID ? null : (
            <div className={classes.actionCell}>
              <EDButton
                size="small"
                wordBreak={false}
                onClick={() => {
                  handleOnDrill(
                    props.rubrikNiva,
                    props.subLevelNiva,
                    params.row.header,
                    props.selectedAnalysID,
                    props.selectedAnalysCodeprivilegeID,
                    props.urval,
                    props.analysType
                  );
                }}
              >
                Analysera
              </EDButton>
            </div>
          ),
      });
    }

    return mappedColumns;
  }, [
    classes.actionCell,
    classes.greenValue,
    classes.redValue,
    formatCurrencyValue,
    frontendUrl,
    handleOnDrill,
    percentFormatter.displayer,
    props.analysLevel,
    props.analysType,
    props.availableReports,
    props.dates,
    props.rubrikNiva,
    props.selectedAnalysCodeprivilegeID,
    props.selectedAnalysID,
    props.subLevelNiva,
    props.tableRows,
    props.sums,
    props.urval,
    popoverState.anchorEl,
    popoverState.rowId,
  ]);

  return (
    <EDControlledExpansionPanel
      headerColor={theme.customProperties.MenuList.baseBg}
      borderVisible
      expanded={props.expandedTable}
      expandIconColor="primary"
      label={
        <EkonomiskAnalysExpansionLabel
          labelText={TABLE_DISPLAY_LABEL}
          subLabelText={`${capitalizeFirstLetter(
            getMonthText(props.dates.from.month)
          )} ${props.dates.from.year}`}
          iconType="expand_details"
          iconVerticalAlign="sub"
        />
      }
      onChange={(_, newValue) => props.setExpandedTable(newValue)}
      isCompact
    >
      <div className={cx(classes.grid)}>
        <EDDataGrid
          rowHeight={42}
          rows={sortedRows}
          columns={columns}
          disableColumnMenu
          disableColumnReorder
          disableMultipleColumnsSorting
          sortModel={sortModel}
          onSortModelChange={model => {
            setSortModel(model);
          }}
          getRowClassName={params =>
            cx({
              [classes.oddRow]: params.indexRelativeToCurrentPage % 2 === 1,
              [classes.evenRow]: params.indexRelativeToCurrentPage % 2 === 0,
            })
          }
        />
      </div>
    </EDControlledExpansionPanel>
  );
};
